import { useCurrentOrg, useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { useAuth, useClearCache, useFeatureFlagsModal, useNavigateToFloorPlanEditorDemo, useSuperAdminToolsModal } from '@/Client/State';
import { AdminChromeProfileMenu, FlagIcon, LockIcon } from '@kaiizen/kai-shiki/react';

declare global {
  interface Window {
    FrontChat?: (action: string, options?: unknown) => unknown;
  }
}

const startupTime = Date.now();

type ProfileMenuAction = 'profile' | 'support' | 'logout';

type ProfileDropdownMenuProps = {
  selectedAction?: ProfileMenuAction;
};

export const ProfileDropdownMenu = ({
  selectedAction,
}: ProfileDropdownMenuProps) => {
  const { credentials, credentialsMutators, isSuperAdmin } = useAuth();
  const { openFeatureFlagsModal } = useFeatureFlagsModal();
  const { openSuperAdminTools } = useSuperAdminToolsModal();
  const { currentOrg } = useCurrentOrg();
  const { featureFlags } = useFeatureFlags();
  const navigateToFloorPlanEditorDemo = useNavigateToFloorPlanEditorDemo();
  const clearCache = useClearCache();
  const navigate = useNavigate();
  const [isSupportVisible, setIsSupportVisible] = useState(false);
  const isSupportVisibleRef = useRef(isSupportVisible);
  const isFrontChatReadyRef = useRef(false);
  const firstPossibleFrontChatCallTimeRef = useRef(startupTime + 500);

  isSupportVisibleRef.current = isSupportVisible;

  useEffect(() => {
    let isCancelled = false;
    let unbind: undefined | (() => void);
    let retryTimeout: undefined | ReturnType<typeof setTimeout>;

    const tryBindVisibilityChange = () => {
      if (isCancelled) return;

      try {
        const maybeUnbind = window.FrontChat?.(
          'onWindowVisibilityChanged',
          (state: { is_window_visible?: boolean }) => {
            isSupportVisibleRef.current = !!state.is_window_visible;
            setIsSupportVisible(isSupportVisibleRef.current);
          },
        );

        if (typeof maybeUnbind === 'function') {
          unbind = maybeUnbind as () => void;
        }
        isFrontChatReadyRef.current = true;
      } catch {
        retryTimeout = setTimeout(tryBindVisibilityChange, 250);
      }
    };

    const initialDelayTimeout = setTimeout(tryBindVisibilityChange, 500);

    return () => {
      isCancelled = true;
      clearTimeout(initialDelayTimeout);
      if (retryTimeout) clearTimeout(retryTimeout);
      unbind?.();
    };
  }, []);

  const displayName = credentials.user?.name?.trim() || 'Kai User';
  const orgName = currentOrg?.name?.trim() || credentials.orgId || '';
  const activeAction = selectedAction ?? (isSupportVisible ? 'support' : undefined);

  const handleMyProfile = () => {
    navigate('/account');
  };

  const handleHelpSupport = () => {
    const callFrontChat = (action: 'show' | 'hide') => {
      try {
        window.FrontChat?.(action);
      } catch {}
    };

    if (isFrontChatReadyRef.current) {
      callFrontChat(isSupportVisibleRef.current ? 'hide' : 'show');
      return;
    }

    setTimeout(
      () => callFrontChat(isSupportVisibleRef.current ? 'hide' : 'show'),
      Math.max(0, firstPossibleFrontChatCallTimeRef.current - Date.now()),
    );
  };

  const handleLogout = async () => {
    trackEvent(ANALYTICS_EVENTS.userLogout);
    await credentialsMutators.clearToken();
    await clearCache();
    navigate('/');
  };

  if (!credentials.user) return null;

  return (
    <AdminChromeProfileMenu
      displayName={displayName}
      orgLabel={orgName}
      selectedAction={activeAction}
      onProfileClick={handleMyProfile}
      onSupportClick={handleHelpSupport}
      onLogoutClick={handleLogout}
      extraItems={[
        ...(featureFlags.floorPlanEditorAdvanced
          ? [{ action: 'floorPlanEditorDemo', label: 'Floor plan editor demo', onClick: () => navigateToFloorPlanEditorDemo() }]
          : []),
        ...(isSuperAdmin
          ? [
            { action: 'featureFlags', label: 'Feature Flags', icon: <FlagIcon />, onClick: openFeatureFlagsModal },
            { action: 'superAdmin', label: 'Super Admin', icon: <LockIcon />, onClick: () => openSuperAdminTools() },
          ]
          : []),
      ]}
    />
  );
};
